import { Alert, Button, Divider, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { updateFundraiserAutogiroReminderSettings } from 'src/shared/services/fundraiserService';
import { FundraiserResponse } from '../models/fundraiserResponse';

type FundraiserAutogiroRemindersProps = {
  fundraiserResponse: FundraiserResponse;
};

type AutogiroRemindersForm = {
  reminder1Subject: string;
  reminder1Body: string;
  reminder2Subject: string;
  reminder2Body: string;
};

// Platshållare som kan användas i ämne och text för påminnelserna.
const reminderCustomFields = [
  { label: 'Namn', value: '[name]' },
  { label: 'Belopp', value: '[amount]' },
  { label: 'Organisation', value: '[organization]' },
  { label: 'Signeringslänk', value: '[signingLink]' },
];

export const FundraiserAutogiroReminders = (props: FundraiserAutogiroRemindersProps) => {
  const { fundraiserResponse } = props;
  const { t } = useTranslation(['fundraiser', 'common']);

  const [remindersEnabled, setRemindersEnabled] = useState(fundraiserResponse.autogiroRemindersEnabled ?? false);

  const formMethods = useForm<AutogiroRemindersForm>({
    mode: 'all',
    defaultValues: {
      reminder1Subject: fundraiserResponse.autogiroReminder1Subject ?? '',
      reminder1Body: fundraiserResponse.autogiroReminder1Body ?? '',
      reminder2Subject: fundraiserResponse.autogiroReminder2Subject ?? '',
      reminder2Body: fundraiserResponse.autogiroReminder2Body ?? '',
    },
  });

  const { handleSubmit } = formMethods;

  const onSubmit = async (data: AutogiroRemindersForm) => {
    try {
      await updateFundraiserAutogiroReminderSettings(fundraiserResponse.fundraiser_id, {
        autogiroRemindersEnabled: remindersEnabled,
        autogiroReminder1Subject: data.reminder1Subject,
        autogiroReminder1Body: data.reminder1Body,
        autogiroReminder2Subject: data.reminder2Subject,
        autogiroReminder2Body: data.reminder2Body,
      });
      toast.success(t('common:success.save') as string);
    } catch {
      toast.error(t('common:error.save') as string);
    }
  };

  return (
    <Grid item xs={12}>
      <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
        {t('fundraiser:autogiroReminders.heading')}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={remindersEnabled} onChange={(e) => setRemindersEnabled(e.target.checked)} />}
                label={t('fundraiser:autogiroReminders.enable')}
              />
            </Grid>

            {remindersEnabled && (
              <>
                <Grid item xs={12}>
                  <Alert severity='info'>{t('fundraiser:autogiroReminders.availableFields')}</Alert>
                </Grid>

                {/* Påminnelse 1 */}
                <Grid item xs={12}>
                  <ContentSection>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant='subtitle1' fontWeight='bold'>
                          {t('fundraiser:autogiroReminders.reminder1')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {t('fundraiser:autogiroReminders.reminder1Description')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <ControlledTextField
                          name='reminder1Subject'
                          label={t('fundraiser:autogiroReminders.subject')}
                          size='small'
                          fullWidth
                          sx={{ mt: 1, mb: 2 }}
                        />
                        <ControlledTextEditor name='reminder1Body' rows={30} noMargin customFields={reminderCustomFields} />
                      </Grid>
                    </Grid>
                  </ContentSection>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Påminnelse 2 */}
                <Grid item xs={12}>
                  <ContentSection>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant='subtitle1' fontWeight='bold'>
                          {t('fundraiser:autogiroReminders.reminder2')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {t('fundraiser:autogiroReminders.reminder2Description')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <ControlledTextField
                          name='reminder2Subject'
                          label={t('fundraiser:autogiroReminders.subject')}
                          size='small'
                          fullWidth
                          sx={{ mt: 1, mb: 2 }}
                        />
                        <ControlledTextEditor name='reminder2Body' rows={30} noMargin customFields={reminderCustomFields} />
                      </Grid>
                    </Grid>
                  </ContentSection>
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant='contained' disableElevation type='submit'>
                {t('common:button.save')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  );
};
